/**
 * Frontend auth (#49): JWT stored in localStorage, attached as a Bearer header
 * on every API call. Login exchanges username/password for the token.
 */
const TOKEN_KEY = "cyclecare.token";
const USER_KEY = "cyclecare.user";

export interface AuthUser {
  id: string;
  username: string;
  role: string;
  tenant: string;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/** Decode the JWT payload (display only — not verification). */
function decodeToken(token: string): AuthUser | null {
  try {
    const part = token.split(".")[1];
    if (part === undefined) return null;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const p = JSON.parse(json) as { sub?: string; username?: string; role?: string; tenant?: string };
    if (p.sub === undefined || p.username === undefined) return null;
    return { id: p.sub, username: p.username, role: p.role ?? "end_user", tenant: p.tenant ?? "" };
  } catch {
    return null;
  }
}

export function getUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (raw !== null) {
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      /* fall through to token decode */
    }
  }
  // Fallback: derive identity from the JWT (covers tokens stored before the
  // user object was persisted).
  const token = getToken();
  return token !== null ? decodeToken(token) : null;
}

/** Clear the session (logout). */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Handle a 401 from an authed request: drop the stale token and send the user to
 * the login screen at "/". Authed pages (scheduler, admin, review) live on their
 * own routes under RequireAdmin/RequireReviewer; navigating away unmounts them so
 * their background streams/reloads stop — turning an endless 401 storm (e.g. the
 * scheduler reloading on every SSE event with an expired token) into ONE 401 and
 * a re-login. Guarded so concurrent 401s don't redirect twice.
 */
export function handleAuthExpired(): void {
  if (getToken() === null) return; // already handled — don't redirect twice
  clearToken();
  window.location.assign("/");
}

/** Merge the Bearer header into a headers object when a token is present. */
export function authHeaders(base: Record<string, string> = {}): Record<string, string> {
  const token = getToken();
  return token !== null ? { ...base, Authorization: `Bearer ${token}` } : base;
}

export type LoginResult =
  | { ok: true; user: AuthUser }
  | { ok: false; reason: string };

export async function login(username: string, password: string): Promise<LoginResult> {
  try {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password })
    });
    if (!res.ok) {
      return { ok: false, reason: res.status === 401 ? "用户名或密码错误" : `登录失败 (HTTP ${res.status})` };
    }
    const body = (await res.json()) as { token: string; user: AuthUser };
    setToken(body.token);
    localStorage.setItem(USER_KEY, JSON.stringify(body.user));
    return { ok: true, user: body.user };
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : String(err) };
  }
}

export type CreateUserResult = { ok: true } | { ok: false; reason: string };

/** Admin-only: create a user account (no public registration). */
export async function createUser(input: {
  username: string;
  password: string;
  tenant: string;
  role: string;
  clearance?: "public" | "internal";
}): Promise<CreateUserResult> {
  try {
    const res = await fetch("/api/admin/users", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(input)
    });
    if (res.ok) return { ok: true };
    if (res.status === 403) return { ok: false, reason: "无权限（需要管理员）" };
    if (res.status === 409) return { ok: false, reason: "用户名已存在或无效" };
    if (res.status === 400) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (body.error === "unknown_tenant") return { ok: false, reason: "租户无效（请从下拉中选择）" };
    }
    return { ok: false, reason: `创建失败 (HTTP ${res.status})` };
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : String(err) };
  }
}

/** A predefined tenant (#93): the audit-stable key plus an optional label. */
export interface Tenant {
  name: string;
  displayName: string | null;
}

/** Admin-only: the predefined tenants for the create-user dropdown (#93). */
export async function listTenants(): Promise<Tenant[]> {
  try {
    const res = await fetch("/api/admin/tenants", { headers: authHeaders() });
    if (!res.ok) return [];
    const body = (await res.json()) as { tenants: Tenant[] };
    return body.tenants;
  } catch {
    return [];
  }
}

/** Admin-only: add a predefined tenant (#93). */
export async function createTenant(
  name: string,
  displayName?: string
): Promise<CreateUserResult> {
  try {
    const res = await fetch("/api/admin/tenants", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ name, ...(displayName !== undefined ? { displayName } : {}) })
    });
    if (res.ok) return { ok: true };
    if (res.status === 403) return { ok: false, reason: "无权限（需要管理员）" };
    return { ok: false, reason: `新增失败 (HTTP ${res.status})` };
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : String(err) };
  }
}

/** A user row shown in the admin management page (#84). */
export interface AdminUser {
  id: string;
  username: string;
  role: string;
  tenant: string;
  isActive: boolean;
  createdAt: string;
}

/** Admin-only: list every user for the management page (#84). */
export async function listUsers(): Promise<AdminUser[]> {
  const res = await fetch("/api/admin/users", { headers: authHeaders() });
  if (!res.ok) throw new Error(`列出用户失败 (HTTP ${res.status})`);
  const body = (await res.json()) as { users: AdminUser[] };
  return body.users;
}

/** Admin-only: delete a user account (#84). Sensitive — caller confirms first. */
export async function deleteUser(userId: string): Promise<CreateUserResult> {
  try {
    const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}`, {
      method: "DELETE",
      headers: authHeaders()
    });
    if (res.ok) return { ok: true };
    if (res.status === 403) return { ok: false, reason: "无权限（需要管理员）" };
    if (res.status === 400) return { ok: false, reason: "不能删除当前登录的管理员账户" };
    if (res.status === 404) return { ok: false, reason: "用户不存在" };
    return { ok: false, reason: `删除失败 (HTTP ${res.status})` };
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : String(err) };
  }
}

export type ResetPasswordResult =
  | { ok: true; password: string }
  | { ok: false; reason: string };

/** Admin-only: reset a user's password (#84). Returns the new password once. */
export async function resetPassword(userId: string): Promise<ResetPasswordResult> {
  try {
    const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/reset-password`, {
      method: "POST",
      headers: authHeaders()
    });
    if (res.ok) {
      const body = (await res.json()) as { password: string };
      return { ok: true, password: body.password };
    }
    if (res.status === 403) return { ok: false, reason: "无权限（需要管理员）" };
    if (res.status === 404) return { ok: false, reason: "用户不存在" };
    return { ok: false, reason: `重置失败 (HTTP ${res.status})` };
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : String(err) };
  }
}
